/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import {
	ReconContainerBatchDetails,
	ReconContainerListBatchResponse,
} from "./reconcileContainer.types";
import Pagination from "../../../../widgets/pagination";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReconTitleHeader from "../common/ReconTitleHeader";
import ReconStatCard from "../common/ReconStatsCard";
import ReconSearchFilter from "../common/ReconSearchFilter";
import MediaPreparationTable from "../../../common/commonComponents/mediaPreparationTable";
import { getContainerReconciliationColumns } from "./partials/columns";
import { mediaReconciliationService } from "../../../../service/mediaReconciliationServices";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import { userService } from "../../../../service/userService";


const ReconcileContainer: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [containerBatchDetails, setContainerBatchDetails] = useState<
		ReconContainerBatchDetails[]
	>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [totalTableElements, setTotalTableElements] = useState<number>(0);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [stats, setStats] = useState({
		pendingCount: 0,
		auditStatus: "-",
		totalUnitsTracked: 0,
	});


	const [sort, setSort] = useState("id");
	  const [order, setOrder] = useState("desc");
	  const mapStatusToApi = (status: string) => {
		if (status === "Expired") return "expired";
		return status;
	  };
	
	  const handleSort = (key: string, value: boolean) => {
		setSort(key);
		setOrder(value ? "asc" : "desc");
		setCurrentPage(1);
	  };
	  
	const [statusFilter, setStatusFilter] = useState("");
	const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);

	const getContainerBatchDetails = async () => {
		setLoading(true);
		try {
			const queryParams = {
				page: currentPage.toString(),
				size: pageSize.toString(),
				sort,
				order,
				keyword: searchTerm,
				reconciliation_status: statusFilter,
			};
			const response: ReconContainerListBatchResponse =
				await mediaReconciliationService.getReconContainerBatchList(
					queryParams,
				);
				setContainerBatchDetails(response.data);
				setTotalTableElements(response.totalElements);
				setTotalPages(response.totalPages);
				setStats({
					pendingCount: response.pending_count,
					auditStatus: response.general_audit_status,
					totalUnitsTracked: response.total_units_tracked,
				});
		} catch (error) {
			alert("Error getting data.");
		}
		finally {
			setLoading(false);
		}
	};

	const handlePageChange = (page: number, size?: number) => {
		setCurrentPage(page);

		if (size) {
			setPageSize(size);
		}
	};
	useEffect(() => {
		getContainerBatchDetails();
	}, [currentPage, pageSize, searchTerm, statusFilter, sort, order]);
	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaReconcile.container.title")} pageDescription={t('pageDescription.containerReconciliation.description')} />
			<div className="p-8 max-w-full animate-in fade-in duration-500 min-h-full bg-[#F8FAFC]">
				{/* <ReconTitleHeader
					title={t("mediaReconcile.container.title")}
					infoText={t("mediaReconcile.container.infoText")}
				/> */}

				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
					<ReconStatCard
						label={t("mediaReconcile.container.stats.pendingCount")}
						value={stats.pendingCount}
						sublabel={t(
							"mediaReconcile.container.stats.lotsRequiringPhy",
						)}
						variant="light"
						accent="blue"
					/>

					<ReconStatCard
						label={t("mediaReconcile.container.stats.auditStatus")}
						value={stats.auditStatus}
						sublabel={t(
							"mediaReconcile.container.stats.monthlyInventoryCheck",
						)}
						variant="light"
						accent="yellow"
					/>

					<ReconStatCard
						label={t("mediaReconcile.container.stats.totalUnitsTracked")}
						value={stats.totalUnitsTracked}
						sublabel={t(
							"mediaReconcile.container.stats.sterilePrimaryPackaging",
						)}
						variant="dark"
					/>
				</div>

				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
					<div className="px-6 py-2 bg-slate-50/30 border-b border-gray-200 flex items-center justify-start gap-2">
						<div className="flex items-center gap-1 text-sm text-gray-700">
							<span className="font-semibold">
								{t("mediaReconcile.title")}
							</span>
							<span className="text-gray-400 font-light">|</span>
							<span className="text-gray-500">{totalTableElements}</span>
						</div>
						<ReconSearchFilter
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							searchPlaceholder={t(
								"mediaReconcile.container.searchPlaceHolder",
							)}
							statusFilter={statusFilter}
							setStatusFilter={(value) => {
								setStatusFilter(value);
								setCurrentPage(1);
							}}
						/>
					</div>

					<MediaPreparationTable
						columns={getContainerReconciliationColumns(t)}
						data={containerBatchDetails}
						loading={loading}
						currentPage={currentPage}
						pageSize={pageSize}
						onSortClick={handleSort}
						emptyMessage={t("mediaReconcile.noDataToDisplay")}
						renderAction={(row) => (
							permission?.includes("start_container_reconciliation") ? (
							<button
								onClick={() =>
									navigate(
										`/startMediaReconciliation/${row.batch_id}/container/${row.reconciliation_status}`,
									)
								}
								className="px-6 py-2 bg-[#FF7043] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none"
							>
								{t("mediaReconcile.container.startCount")}
							</button>
							): null
						)}
					/>
				</div>
			</div>
			<Pagination
				page={currentPage}
				totalPages={totalPages}
				handlePageChange={handlePageChange}
			/>
		</div>
	);
};

export default ReconcileContainer;
